/**
 * The widget's conversation surface: message log, empty state, composer.
 *
 * Assistant answers render through `AlfieContent variant="chat"` so citation
 * tokens resolve exactly as they do on /alfie and in the community feed — a
 * plain markdown renderer would leak raw `[[alias:uuid|label]]` tokens.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlfieTypingDots } from '@/components/alfie/AlfieTypingDots';
import { ArrowLeftRight, Check, ChevronDown, Copy, FileText, ListOrdered, Lock, Target, TrendingUp, Users, Wallet } from 'lucide-react';
import { Link } from '@tanstack/react-router';

import AlfieContent, {
  ENTITY_TYPE_CHIP_ICONS,
  ENTITY_TYPE_LABELS,
} from '@/components/community/AlfieContent';
import HearthstoneIcon from '@/components/icons/HearthstoneIcon';
import type { ResolvedCitation } from '@/api/alfie';
import { trackWidget } from './widget-analytics';
import { PlanCards } from '@/components/alfie/PlanCards';
import { formatDateWrittenShort, formatTime, toDateKey } from '@/lib/dates';

import { WidgetComposer } from './WidgetComposer';
import type { WidgetMessage } from './use-widget-chat';

const STARTERS = [
  { icon: TrendingUp, text: "What's my Prime Score and what moves it?" },
  { icon: Target, text: 'What should I focus on this week?' },
  { icon: Users, text: 'What did the community discuss this week?' },
] as const;

/** On the Xero screens (finance mode), Tim's first four (31 Oct 2026). */
const FINANCE_STARTERS = [
  { icon: TrendingUp, text: 'How is my business tracking this month?' },
  { icon: Wallet, text: "What's my cash position, and how long will it last?" },
  { icon: ArrowLeftRight, text: 'Why is my profit different from the cash in the bank?' },
  { icon: ListOrdered, text: 'Which figure should I look at first?' },
] as const;

/**
 * Finance mode's controls above the composer. `topicLabel` is what the next question is about (null
 * = the figures only); `pending` is a question held back because two topics fit equally well.
 */
export interface WidgetFinanceBar {
  topicLabel: string | null;
  /** Called when the topic list is opened, so it always shows what is on screen now. */
  listTopics: () => { id: string; label: string }[];
  onChooseTopic: (id: string | null) => void;
  pending: { text: string; options: { id: string; label: string }[] } | null;
  onPendingPick: (id: string | null) => void;
}

const OVERLINE = 'text-[11px] font-semibold uppercase leading-[14px] tracking-[0.06em] text-muted-foreground';

interface WidgetChatViewProps {
  messages: WidgetMessage[];
  isTyping: boolean;
  isLoadingHistory: boolean;
  completedReplies: number;
  firstName: string;
  draft: string;
  onDraftChange: (value: string) => void;
  onSend: (text: string, fileIds?: string[]) => void;
  /** Aborts the in-flight answer, keeping whatever has streamed so far. */
  onStop: () => void;
  composerRef: React.RefObject<HTMLTextAreaElement>;
  /** Present in finance mode only. */
  finance?: WidgetFinanceBar | null;
}

/** "Today" / "Yesterday" / written date, matching AlfieChatView's separators. */
function dayLabel(ts: string): string {
  const key = toDateKey(ts);
  const now = new Date();
  if (key === toDateKey(now)) return 'Today';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (key === toDateKey(yesterday)) return 'Yesterday';
  return formatDateWrittenShort(ts);
}

function DaySeparator({ label }: { label: string }) {
  return (
    <div className={`flex items-center gap-2.5 ${OVERLINE}`}>
      <span aria-hidden className="h-px flex-1 bg-border" />
      {label}
      <span aria-hidden className="h-px flex-1 bg-border" />
    </div>
  );
}

/** Expandable list behind the "N sources" chip. Mirrors AlfieChatView. */
function SourcesList({ citations }: { citations: ResolvedCitation[] }) {
  return (
    <div className="mt-2 rounded-xl border border-border bg-muted/50 px-3 py-2">
      <p className={`mb-1 ${OVERLINE}`}>Sources</p>
      <ul className="space-y-0.5">
        {citations.map((c) => {
          const Icon = ENTITY_TYPE_CHIP_ICONS[c.entityType] ?? FileText;
          const typeLabel = ENTITY_TYPE_LABELS[c.entityType] ?? 'Resource';
          const row = (
            <span className="flex items-center gap-2.5 py-1">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                {c.state === 'locked' ? (
                  <Lock className="h-3.5 w-3.5" />
                ) : (
                  <Icon className="h-3.5 w-3.5" />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold text-foreground">
                  {(c.label || c.name).trim()}
                </span>
                <span className="block text-[11px] text-muted-foreground">
                  {c.state === 'locked' ? `${typeLabel} - upgrade to access` : typeLabel}
                </span>
              </span>
            </span>
          );
          const key = `${c.entityType}|${c.entityId}`;
          if (c.state === 'available' && c.url?.startsWith('/') && !c.url.startsWith('//')) {
            return (
              <li key={key}>
                <Link
                  to={c.url}
                  className="block rounded-lg px-1 transition-colors hover:bg-foreground/[0.04]"
                >
                  {row}
                </Link>
              </li>
            );
          }
          if (c.state === 'available' && /^https?:\/\//i.test(c.url ?? '')) {
            return (
              <li key={key}>
                <a
                  href={c.url as string}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block rounded-lg px-1 transition-colors hover:bg-foreground/[0.04]"
                >
                  {row}
                </a>
              </li>
            );
          }
          return (
            <li key={key} className="px-1 opacity-80">
              {row}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** The shape this file used to own; now shared so the three Alfie surfaces cannot drift apart. */
function TypingDots() {
  return <AlfieTypingDots />;
}

function EmptyState({
  firstName,
  onPick,
  finance = false,
}: { firstName: string; onPick: (text: string) => void; finance?: boolean }) {
  const starters = finance ? FINANCE_STARTERS : STARTERS;
  return (
    // `flex-1 min-h-0`, never `h-full`: `h-full` resolves to the whole panel
    // body and shoves the composer past the panel's bottom edge.
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2.5 px-8 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/15">
          <HearthstoneIcon size={28} variant="brand" />
        </div>
        <p className="pt-1.5 text-[22px] font-semibold leading-[28px] tracking-[-0.01em] text-foreground">
          Hi{firstName ? ` ${firstName}` : ''}.
        </p>
        <p className="text-[15px] leading-[22px] text-muted-foreground">
          {finance
            ? 'Ask about your figures, your accounts and what they mean.'
            : 'Ask about your business, your courses, or what the community is saying.'}
        </p>
      </div>
      <div className="shrink-0 px-4 pb-2">
        <p className={`px-1 pb-1.5 ${OVERLINE}`}>Try asking</p>
        {starters.map(({ icon: Icon, text }) => (
          <button
            key={text}
            type="button"
            onClick={() => {
              trackWidget({ action: 'prompt_clicked', prompt: text });
              onPick(text);
            }}
            className="flex h-12 w-full items-center gap-3 border-t border-border px-1 text-left transition-colors hover:bg-foreground/[0.04]"
          >
            <Icon className="h-[18px] w-[18px] shrink-0 text-primary" strokeWidth={1.75} />
            <span className="min-w-0 flex-1 truncate text-[15px] leading-[18px] text-foreground">
              {text}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function WidgetChatView({
  messages,
  isTyping,
  isLoadingHistory,
  completedReplies,
  firstName,
  draft,
  onDraftChange,
  onSend,
  onStop,
  composerRef,
  finance = null,
}: WidgetChatViewProps) {
  const endRef = useRef<HTMLDivElement>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [openSources, setOpenSources] = useState<Set<string>>(new Set());
  const [announcement, setAnnouncement] = useState('');

  // Announce completion ONCE per reply, never per token — a live region fed by
  // the streaming text would read the answer character by character.
  useEffect(() => {
    if (completedReplies === 0) return;
    setAnnouncement('Alfie finished answering');
    const timer = setTimeout(() => setAnnouncement(''), 1500);
    return () => clearTimeout(timer);
  }, [completedReplies]);

  useEffect(() => {
    const streaming = messages.some((m) => m.streaming);
    endRef.current?.scrollIntoView?.({ behavior: streaming ? 'auto' : 'smooth' });
  }, [messages, isTyping]);

  const toggleSources = useCallback((id: string) => {
    setOpenSources((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleCopy = useCallback((id: string, content: string) => {
    void navigator.clipboard?.writeText(content).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 1500);
    });
  }, []);

  /** True on the first message of each calendar day. */
  const separatorAt = useMemo(() => {
    const flags: boolean[] = [];
    let lastKey = '';
    for (const m of messages) {
      const key = m.timestamp ? toDateKey(m.timestamp) : '';
      flags.push(!!key && key !== lastKey);
      if (key) lastKey = key;
    }
    return flags;
  }, [messages]);

  const isEmpty = messages.length === 0 && !isTyping && !isLoadingHistory;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <span className="sr-only" role="status" aria-live="polite">
        {announcement}
      </span>

      {isEmpty ? (
        <EmptyState firstName={firstName} onPick={(text) => onSend(text)} finance={!!finance} />
      ) : (
        <div
          role="log"
          // `role="log"` is an implicitly POLITE live region, so a screen
          // reader would read the answer token by token as it streams. The
          // sr-only status above stays the single announcement.
          aria-live="off"
          aria-relevant="additions"
          aria-label="Conversation with Alfie"
          className="flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 pb-2 pt-4"
        >
          {isLoadingHistory && messages.length === 0 && (
            <div className="space-y-3" aria-hidden>
              <div className="ml-auto h-10 w-2/3 animate-pulse rounded-2xl bg-muted" />
              <div className="h-16 w-full animate-pulse rounded-xl bg-muted" />
            </div>
          )}

          {messages.map((msg, index) => {
            // Pre-first-chunk the dots below own the loading state.
            if (msg.role === 'assistant' && msg.streaming && !msg.content) return null;
            const citations = msg.citations ?? [];
            return (
              <div key={msg.id} className="space-y-4">
                {separatorAt[index] && msg.timestamp && (
                  <DaySeparator label={dayLabel(msg.timestamp)} />
                )}
                {msg.role === 'user' ? (
                  <div className="flex justify-end">
                    <div className="max-w-[80%] rounded-2xl rounded-br-md bg-primary px-3.5 py-2.5 text-primary-foreground">
                      <p className="whitespace-pre-wrap text-sm leading-[21px]">{msg.content}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <AlfieContent variant="chat" content={msg.content} citations={msg.citations} />
                    {/* Plan cards under a package answer, once it has finished streaming. */}
                    {!msg.streaming && (msg.planCards?.length ?? 0) > 0 && (
                      <PlanCards cards={msg.planCards} surface="widget" compact />
                    )}
                    {!msg.streaming && (
                      <div className="flex items-center gap-1.5 pt-0.5 text-muted-foreground">
                        <button
                          type="button"
                          onClick={() => handleCopy(msg.id, msg.content)}
                          aria-label="Copy answer"
                          className="flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
                        >
                          {copiedId === msg.id ? (
                            <Check className="h-[15px] w-[15px]" />
                          ) : (
                            <Copy className="h-[15px] w-[15px]" />
                          )}
                        </button>
                        {citations.length > 0 && (
                          <button
                            type="button"
                            onClick={() => toggleSources(msg.id)}
                            aria-expanded={openSources.has(msg.id)}
                            className="inline-flex h-[26px] items-center gap-1.5 rounded-full border border-border px-2.5 text-xs font-medium transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
                          >
                            {citations.length === 1 ? '1 source' : `${citations.length} sources`}
                          </button>
                        )}
                        {msg.timestamp && (
                          <span className="ml-auto text-[11px] leading-[14px]">
                            {formatTime(msg.timestamp)}
                          </span>
                        )}
                      </div>
                    )}
                    {openSources.has(msg.id) && citations.length > 0 && (
                      <SourcesList citations={citations} />
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {isTyping && !messages.some((m) => m.streaming && m.content) && <TypingDots />}
          <div ref={endRef} />
        </div>
      )}

      {finance && <FinanceBar bar={finance} />}

      <WidgetComposer
        value={draft}
        onChange={onDraftChange}
        onSend={onSend}
        onStop={onStop}
        isStreaming={isTyping}
        disabled={isTyping}
        inputRef={composerRef}
      />
    </div>
  );
}

/**
 * "About: <topic>" above the composer, with the list to change it; or, when two topics fit a
 * question equally well, "Which do you mean?" with both and "Just my figures". The question is
 * only sent once one is picked.
 */
function FinanceBar({ bar }: { bar: WidgetFinanceBar }) {
  const [listOpen, setListOpen] = useState(false);
  const chip =
    'inline-flex h-8 max-w-full items-center gap-1 rounded-full border border-border px-3 text-xs font-medium text-foreground transition-colors hover:bg-foreground/[0.06]';

  if (bar.pending) {
    return (
      <div className="shrink-0 space-y-1.5 border-t border-border px-4 py-2" data-finance-ask>
        <p className="text-xs text-muted-foreground">Which do you mean?</p>
        <div className="flex flex-wrap gap-1.5">
          {bar.pending.options.map((o) => (
            <button key={o.id} type="button" className={chip} onClick={() => bar.onPendingPick(o.id)}>
              <span className="truncate">{o.label}</span>
            </button>
          ))}
          <button type="button" className={chip} onClick={() => bar.onPendingPick(null)}>
            Just my figures
          </button>
        </div>
      </div>
    );
  }

  const topics = listOpen ? bar.listTopics() : [];
  return (
    <div className="shrink-0 px-4 pt-2" data-finance-topic>
      <button
        type="button"
        className={chip}
        aria-expanded={listOpen}
        onClick={() => setListOpen((v) => !v)}
      >
        <span className="text-muted-foreground">About:</span>
        <span className="truncate">{bar.topicLabel ?? 'Your figures'}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </button>
      {listOpen && (
        <ul className="mt-1.5 max-h-48 overflow-y-auto rounded-lg border border-border" role="listbox">
          {[{ id: null as string | null, label: 'Just my figures' }, ...topics].map((t) => (
            <li key={t.id ?? 'none'}>
              <button
                type="button"
                role="option"
                aria-selected={(t.id === null && bar.topicLabel === null) || t.label === bar.topicLabel}
                onClick={() => {
                  bar.onChooseTopic(t.id);
                  setListOpen(false);
                }}
                className="block w-full truncate px-3 py-2 text-left text-sm transition-colors hover:bg-foreground/[0.04]"
              >
                {t.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default WidgetChatView;
